/**
 * MapSettingsPopover (AK-186, Phase 2 PR-3b) — gear-icon trigger в left
 * `CropFilterPanel`, разворачивает Popover с 4 toggle'ами подписей карты.
 *
 * Decision: Path B per Decisions log v3.2 (Confluence page 39682049,
 * Escalation #2). Wraps EXISTING 4 toggles из `CropFilterPanel.tsx:182-197`,
 * не вводит новые. Цель — уменьшить визуальный шум секции «Подписи на карте».
 *
 * State source: `useMapStore` (та же что и раньше). Никаких новых полей в store.
 *
 * Tokens-only (Earth Tech) — bg-popover / text-foreground / border-border
 * через `Popover` primitive + `Switch`/`Button` shadcn primitives.
 *
 * A11y:
 *   - Trigger: aria-label="Настройки подписей на карте", focus-visible ring
 *     (унаследовано от Button variant=ghost), touch target ≥44px (h-11 w-11).
 *   - Switch: каждый label обёрнут <label> для click-on-text проксирования.
 *   - Popover Content: align="end", side="bottom" — anchor к gear, открытие вниз
 *     и влево чтобы не вылетать из узкого left rail.
 *
 * Mobile responsive Sheet wrap — out of scope этого PR (см. Jira AK-186).
 */
import { Settings } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useMapStore } from '@/lib/map/useMapStore';

export function MapSettingsPopover() {
  const fieldNumberOn = useMapStore((s) => s.fieldNumberOn);
  const cropNameOn = useMapStore((s) => s.cropNameOn);
  const cropIconsOn = useMapStore((s) => s.cropIconsOn);
  const fieldAreaOn = useMapStore((s) => s.fieldAreaOn);
  const setFieldNumberOn = useMapStore((s) => s.setFieldNumberOn);
  const setCropNameOn = useMapStore((s) => s.setCropNameOn);
  const setCropIconsOn = useMapStore((s) => s.setCropIconsOn);
  const setFieldAreaOn = useMapStore((s) => s.setFieldAreaOn);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          // Touch target ≥44px (WCAG 2.5.5). size="icon" default h-10 w-10 (40px) —
          // explicit override для tap surface на мобиле.
          className="h-11 w-11 shrink-0"
          aria-label="Настройки подписей на карте"
        >
          <Settings className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // align="end" + side="bottom": кнопка справа в row, popover открывается
        // вниз с правым выравниванием — не вылетает за левую границу панели.
        align="end"
        side="bottom"
        sideOffset={6}
        className="w-64"
      >
        <div className="space-y-3">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Подписи на карте
          </div>
          <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
            <span className="text-foreground">Номер поля</span>
            <Switch
              checked={fieldNumberOn}
              onCheckedChange={setFieldNumberOn}
              aria-label="Показывать номер поля"
            />
          </label>
          <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
            <span className="text-foreground">Название культуры</span>
            <Switch
              checked={cropNameOn}
              onCheckedChange={setCropNameOn}
              aria-label="Показывать название культуры"
            />
          </label>
          <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
            <span className="text-foreground">Иконка культуры</span>
            <Switch
              checked={cropIconsOn}
              onCheckedChange={setCropIconsOn}
              aria-label="Показывать иконку культуры"
            />
          </label>
          <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
            <span className="text-foreground">Площадь поля</span>
            <Switch
              checked={fieldAreaOn}
              onCheckedChange={setFieldAreaOn}
              aria-label="Показывать площадь поля"
            />
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}
